import { Security, nonEmptyArray, safeAbsurd } from '@ipfabric/utils';
import type { PathLookupOptionsNode } from 'components/SecuritySchema/model/SecuritySchemaNode';
import { dereferenceValue } from 'components/SecuritySchema/model/helpers/dereferenceValue';
import type { SecurityNodeResolutionScope } from 'components/SecuritySchema/model/helpers/securitySchemaNodeFromTest';
import { pipe } from 'fp-ts/lib/function';

/**
 * Resolve literal or referenced value to always literal value
 *
 * Overloaded signature is needed to properly resolve return value from input.
 * When needed to be used for another type, add overload by this pattern:
 * `(value: Security.XxxValue): Security.LiteralXxxValue | undefined;`
 * where `XxxValue = LiteralXXxValue | ReferencedXxxValue`.
 */
const resolveValue =
  (
    resolutionScope: SecurityNodeResolutionScope,
  ): {
    (
      value: Security.NumberSetValue,
    ): Security.LiteralNumberSetValue | undefined;
    (
      value: Security.NumberSetGroupValue,
    ): Security.LiteralNumberSetGroupValue | undefined;
    (
      value: Security.NumberSetOrGroupValue,
    ):
      | Security.LiteralNumberSetValue
      | Security.LiteralNumberSetGroupValue
      | undefined;
    (
      value: Security.StringSetValue,
    ): Security.LiteralStringSetValue | undefined;
    (
      value: Security.StringSetGroupValue,
    ): Security.LiteralStringSetGroupValue | undefined;
    (
      value: Security.StringSetOrGroupValue,
    ):
      | Security.LiteralStringSetValue
      | Security.LiteralStringSetGroupValue
      | undefined;
  } =>
  (value: Security.Value) =>
    (Security.isReferencedValue(value)
      ? dereferenceValue(resolutionScope)(value)
      : value) as any; // type is checked by overload signature above

const numberSetReferenceError: Security.LiteralNumberSetValue = {
  type: 'numberSet',
  original: 'Reference error',
  value: [],
};

const stringSetReferenceError: Security.LiteralStringSetValue = {
  type: 'stringSet',
  original: 'Reference error',
  value: [],
};

export const securitySchemaNodeFromPathLookupOptions =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (pathLookupOptions: Security.PathLookupOption[]): PathLookupOptionsNode => ({
    type: 'pathLookupOptions',
    // dereference any nested Security.Value instances in the type here
    pathLookupOptions: pathLookupOptions.map((option) => {
      switch (option.type) {
        case 'natIpv4Translated':
          return {
            ...option,
            value:
              resolveValue(resolutionScope)(option.value) ??
              numberSetReferenceError,
          };
        case 'natIpv4L3IntPrimaryIp':
          return option;
        case 'natIpv4RoutingOutL3IntPrimaryIp':
          return option;
        case 'natIpv4LoadBalancingDnat':
          return {
            ...option,
            value: pipe(
              option.value,
              nonEmptyArray.map(
                (item): Security.NatIpv4LoadBalancingTarget => ({
                  toIp:
                    resolveValue(resolutionScope)(item.toIp) ??
                    numberSetReferenceError,
                  toPort: item.toPort
                    ? (resolveValue(resolutionScope)(item.toPort) ??
                      numberSetReferenceError)
                    : undefined,
                }),
              ),
            ),
          };
        case 'nat46StatelessToPrefix':
          return option;
        case 'nat64StatelessDstFromPrefix':
          return option;
        case 'natIpv6Translated':
          return {
            ...option,
            value:
              resolveValue(resolutionScope)(option.value) ??
              stringSetReferenceError,
          };
        case 'natIpv6L3IntPrimaryIp':
          return option;
        case 'natIpv6RoutingOutL3IntPrimaryIp':
          return option;
        case 'pbrInterface':
        case 'pbrInterfaceDefault':
        case 'pbrIpv4Nexthops':
        case 'pbrIpv4NexthopsDefault':
        case 'pbrIpv4NexthopsRecursive':
        case 'pbrIpv4NexthopsVerifyAvailability':
        case 'pbrMacNexthops':
        case 'pbrVrf':
          return option;
        default:
          safeAbsurd(option);
          return option;
      }
    }),
    children: [],
  });
